export interface TableSettings {
  tableId: string;
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  columnSizing?: Record<string, number>;
  sorting?: { id: string; desc: boolean }[];
  pageSize?: number;
  selectedRows?: string[];
  compactView?: boolean;
}

class TableSettingsStore {
  private db: IDBDatabase | null = null;
  private readonly dbName = 'campaign-manager-table-settings';
  private readonly version = 1;
  private readonly storeName = 'tableSettings';

  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'tableId' });
        }
      };
    });
  }

  async init(): Promise<void> {
    if (this.db) return;

    try {
      this.db = await this.openDB();
    } catch (error) {
      console.error('Failed to initialize table settings store:', error);
    }
  }

  async getSettings(tableId: string): Promise<TableSettings | null> {
    await this.init();
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(tableId);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => {
          console.error('Failed to get table settings:', request.error);
          resolve(null);
        };
      } catch (error) {
        console.error('Failed to get table settings:', error);
        resolve(null);
      }
    });
  }

  async saveSettings(settings: TableSettings): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put(settings);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Failed to save table settings:', request.error);
          resolve();
        };
      } catch (error) {
        console.error('Failed to save table settings:', error);
        resolve();
      }
    });
  }

  async deleteSettings(tableId: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(tableId);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Failed to delete table settings:', request.error);
          resolve();
        };
      } catch (error) {
        console.error('Failed to delete table settings:', error);
        resolve();
      }
    });
  }

  async getAllSettings(): Promise<TableSettings[]> {
    await this.init();
    if (!this.db) return [];

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => {
          console.error('Failed to get all table settings:', request.error);
          resolve([]);
        };
      } catch (error) {
        console.error('Failed to get all table settings:', error);
        resolve([]);
      }
    });
  }

  cleanup(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

export const tableSettingsStore = new TableSettingsStore();
